import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';

export interface IWaitlistItemEntityAttributes extends IModelAttributes {
	bookingType: Enums.bookingType;
	removed: boolean;
	email: string;

	cargoTypeId?: string;
	cargoType?: Models.CargoTypeEntity | Models.ICargoTypeEntityAttributes;
	/** The fare type waited for, or undefined for any. */
	passengerClassId?: string;
	/**
	 * The fare type's name, flattened onto the row by `WaitlistItemEntity.ToDto()`.
	 *
	 * <p>Absent on the staff list, which is served by the generic entity endpoint and sends the whole
	 * `passengerClass` object instead — so read that as a fallback rather than this alone.</p>
	 */
	passengerClassName?: string;
	passengerClass?: { name?: string } | null;
	/** How many are travelling. Undefined means one. */
	partySize?: number;
	/** False to hold the email back until the whole party fits. Undefined reads as true. */
	notifyOnPartialSpace?: boolean;
	ferryTripId: string;
	ferryTrip: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
	isStandby?: boolean;
	name?: string;
	phone?: string;
}
@entity('WaitlistItemEntity', 'Waitlist Item')
export default class WaitlistItemEntity extends Model implements IWaitlistItemEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<WaitlistItemEntity, Enums.bookingType>()
	public bookingType: Enums.bookingType;

	/**
	 * True when user has opted out of the notifications.
	 */
	@observable
	@attribute<WaitlistItemEntity, boolean>()
	public removed: boolean;

	@observable
	@attribute<WaitlistItemEntity, string>()
	public email: string;

	@observable
	@attribute<WaitlistItemEntity, boolean>()
	public isStandby: boolean;

	@observable
	@attribute<WaitlistItemEntity, string>()
	public name: string;

	@observable
	@attribute<WaitlistItemEntity, string>()
	public phone: string;

	@observable
	@attribute()
	public cargoTypeId?: string;

	@observable
	@attribute()
	public passengerClassId?: string;

	@observable
	@attribute()
	public passengerClassName?: string;

	/**
	 * The fare type as the generic entity endpoint sends it — the whole object, no flattened name.
	 * Typed structurally: fare types are not in the generated `Models`, and only the name is read.
	 */
	@observable
	@attribute()
	public passengerClass?: { name?: string } | null;

	@observable
	@attribute()
	public partySize?: number;

	@observable
	@attribute()
	public notifyOnPartialSpace?: boolean;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public cargoType: Models.CargoTypeEntity;

	@observable
	@attribute()
	public ferryTripId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	constructor(attributes?: Partial<IWaitlistItemEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IWaitlistItemEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.bookingType !== undefined) {
				this.bookingType = attributes.bookingType;
			}
			if (attributes.removed !== undefined) {
				this.removed = attributes.removed;
			}
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.isStandby !== undefined) {
				this.isStandby = attributes.isStandby;
			}
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.cargoTypeId !== undefined) {
				this.cargoTypeId = attributes.cargoTypeId;
			}
			if (attributes.cargoType !== undefined) {
				if (attributes.cargoType === null) {
					this.cargoType = attributes.cargoType;
				} else if (attributes.cargoType instanceof Models.CargoTypeEntity) {
					this.cargoType = attributes.cargoType;
					this.cargoTypeId = attributes.cargoType.id;
				} else {
					this.cargoType = new Models.CargoTypeEntity(attributes.cargoType);
					this.cargoTypeId = this.cargoType.id;
				}
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
		}
	}
}
